import { ToggleButton, ToggleButtonGroup, Tooltip } from '@mui/material';
import classNames from 'classnames';
import css from './MultipleToggle.module.scss';

interface MultipleToggleProps<T extends string> {
  options: {
    value: T;
    label: string;
    disabled?: boolean;
    /** Render the option as unavailable: struck-through, `aria-disabled`, with this tooltip
     *  explaining why, and selecting it is ignored. Unlike native `disabled`, the button stays
     *  enabled so the tooltip still fires on hover. */
    disabledTooltip?: React.ReactNode;
    customLabel?: React.ReactNode;
    padding?: number;
  }[];
  value: T;
  onChange: (value: T) => void;
  className?: string;
}

export function MultipleToggle<T extends string>({
  options,
  value,
  onChange,
  className,
}: MultipleToggleProps<T>) {
  return (
    <div className={classNames(css.toggleGroup, className)}>
      <ToggleButtonGroup
        color="primary"
        exclusive
        value={value}
        onChange={(_, newValue) => {
          if (!newValue) return;
          // Ignore selecting an option marked unavailable (it's kept enabled only for its tooltip).
          if (options.find((o) => o.value === newValue)?.disabledTooltip != null) return;
          onChange(newValue);
        }}
      >
        {options.map((option) => {
          const unavailable = option.disabledTooltip != null;
          const labelNode = option.customLabel ?? option.label;
          return (
            <ToggleButton
              key={option.value}
              value={option.value}
              disabled={option.disabled}
              aria-disabled={unavailable || undefined}
              style={
                option.padding
                  ? { paddingLeft: option.padding, paddingRight: option.padding }
                  : undefined
              }
            >
              {unavailable ? (
                <Tooltip title={option.disabledTooltip}>
                  <span className={css.unavailableOption}>{labelNode}</span>
                </Tooltip>
              ) : (
                labelNode
              )}
            </ToggleButton>
          );
        })}
      </ToggleButtonGroup>
    </div>
  );
}
